/**
 * Popular-products PEEK carousel — section-specific Swiper config for <product-recommendations>.
 *
 * Figma parity (Designs mFgU5IzV2QbEKQPrtJPywq · desktop 1655:24744 / mobile 1663:69421):
 * fixed-width slides (258 mobile / 312 desktop — set in _product-recommendations.scss) shown with
 * `slidesPerView: 'auto'` so the NEXT card peeks naturally — ~77px (~30%) on mobile, a 5th card on
 * desktop — using the section side-padding as the first-slide offset (24 / 48) and a wider desktop
 * gap (16 / 32).
 *
 * WHY THIS MODULE EXISTS — the shared carousel init (assets/global.js → `.products-slider`) computes
 * a FRACTIONAL numeric slidesPerView with a fixed 16px gap, which cannot express the Figma
 * fixed-width + 32px-gap peek. We DO NOT touch that shared init (other sections rely on it). Instead
 * we re-point ONLY the Swiper instance that lives inside a <product-recommendations> element to
 * 'auto', and re-apply after the shared init re-creates the instance (it calls `new Swiper(...)` on
 * window resize / section reload, which would otherwise reset our params). Navigation and the
 * recommendations fetch are left completely untouched.
 *
 * Self-initialising side-effect module (matches the src/js/objects/global convention). Idempotent.
 */

// Match the carousel in BOTH source modes — the PDP <product-recommendations> custom element AND the
// homepage collection-sourced wrapper (a plain element carrying .popular-products, no AJAX) — by
// keying on the shared .popular-products class instead of the product-recommendations tag. (The
// vendored Swiper engine + shared init in assets/global.js are untouched; only this section-specific
// peek-config selector is generalised.)
const SWIPER_SELECTOR = '.popular-products .products-slider__swiper.swiper';

// Mirrors $grid-desktop-min in src/scss/variables/_breakpoints.scss — keep in sync.
const DESKTOP_MIN = 1024;

// px values mirror the rem tokens in _product-recommendations.scss. Motto's root is a fixed 10px so
// rem(N) === N px; keep the two in sync if the tokens move.
const GAP = { mobile: 16, desktop: 32 };
const OFFSET = { mobile: 24, desktop: 48 };

function isDesktop() {
  return window.matchMedia(`(min-width: ${DESKTOP_MIN}px)`).matches;
}

// The first-slide inset (Swiper's slidesOffsetBefore) applies ONLY when the carousel is full-bleed.
// In contained mode the `.ub-container` gutter (snippets/section-width.liquid, driven by the
// `full_width`/`layout` setting) already insets the track, so adding the JS offset on top would DOUBLE
// it. The mode is stamped on the `.products-slider` wrapper as data-full-width (absent on the legacy
// PDP products-swiper path -> treated as full-bleed, its historical default). Shared by applyPeek (the
// value handed to Swiper) and syncOverflow (the leading term of the real-overflow measure) so the two
// can never disagree.
function leadingOffset(swiperEl) {
  const sliderWrap = swiperEl.closest('.products-slider');
  const isFullWidth = !sliderWrap || sliderWrap.dataset.fullWidth !== 'false';
  if (!isFullWidth) return 0;
  return isDesktop() ? OFFSET.desktop : OFFSET.mobile;
}

// True once we've already configured this instance for the current viewport class — cheap guard so
// repeated scans are no-ops until the shared init replaces the instance (resetting it to numeric).
function alreadyPeeking(sw) {
  const noBreakpoints =
    sw.params.breakpoints == null ||
    Object.keys(sw.params.breakpoints).length === 0;
  return sw.params.slidesPerView === 'auto' && noBreakpoints;
}

function applyPeek(swiperEl) {
  const sw = swiperEl && swiperEl.swiper;
  if (!sw || sw.destroyed) return;
  if (alreadyPeeking(sw)) return;

  const gap = isDesktop() ? GAP.desktop : GAP.mobile;
  const offset = leadingOffset(swiperEl);

  // Re-point BOTH the live params and originalParams so Swiper's own setBreakpoint()/update() can't
  // reset slidesPerView back to a numeric breakpoint value. Dropping `breakpoints` removes the
  // shared numeric per-view overrides for THIS instance only.
  const patch = (p) => {
    if (!p) return;
    p.slidesPerView = 'auto';
    p.spaceBetween = gap;
    p.slidesOffsetBefore = offset;
    p.breakpoints = {};
  };
  patch(sw.params);
  patch(sw.originalParams);

  // The previous numeric mode set inline widths on each slide; clear them so the CSS fixed widths
  // (258 / 312) take effect under 'auto'.
  swiperEl.querySelectorAll('.swiper-slide').forEach((slide) => {
    slide.style.width = '';
  });

  sw.update();
}

// OVERFLOW GATE — a PEEK carousel whose cards all FIT the container needs no nav and no drag: "the page
// already shows all the cards" -> zero arrows, no swiper affordance. Swiper's own `watchOverflow` lock
// is TOO STRICT here: with `slidesOffsetBefore` set it only locks when the container clears the content
// PLUS twice the offset, so a track that visually fits (last card flush to the edge) still reads as
// "overflowing" and keeps the arrows shown. So we measure real overflow ourselves from the SAME geometry
// Swiper lays out with (fixed card widths + inter-slide gap + leading offset) and gate the nav on it.
// FITS -> hide BOTH arrows outright (display:none !important — the `.popular-products .swiper-button
// :not(.swiper-button-disabled){display:flex !important}` rule in _product-recommendations.scss would
// otherwise keep the active arrow visible) and turn dragging off; OVERFLOWS -> hand nav visibility back
// to Swiper's disabled-class logic (+ the SCSS + the inline changePosition script) and re-enable drag.
function syncOverflow(swiperEl) {
  const sw = swiperEl && swiperEl.swiper;
  if (!sw || sw.destroyed) return;

  const slides = swiperEl.querySelectorAll('.products-slider__slide');
  if (!slides.length) return;

  const gap = isDesktop() ? GAP.desktop : GAP.mobile;
  let content = leadingOffset(swiperEl); // = Swiper's slidesOffsetBefore
  slides.forEach((slide, i) => {
    content += slide.getBoundingClientRect().width;
    if (i < slides.length - 1) content += gap;
  });
  // content = right edge of the last card. If it fits inside the visible container, nothing scrolls.
  const overflows = content > swiperEl.clientWidth + 1; // +1px tolerance for sub-pixel rounding

  sw.allowTouchMove = overflows;

  const wrap = swiperEl.closest('.products-slider__wrapper');
  const buttons = wrap
    ? wrap.querySelectorAll('.swiper-button-prev, .swiper-button-next')
    : [];
  buttons.forEach((el) => {
    if (overflows) {
      el.style.removeProperty('display'); // back to Swiper/SCSS/inline-script control
    } else {
      el.style.setProperty('display', 'none', 'important'); // fits -> no nav affordance at all
    }
  });

  if (!overflows) sw.slideTo(0, 0); // undo any partial scroll left from a previously-wider viewport
}

// Tracks which swiper elements already have a MutationObserver attached, so we don't double-up.
const observed = new WeakSet();

function scan() {
  document.querySelectorAll(SWIPER_SELECTOR).forEach((el) => {
    // Only act once the shared init has actually initialised the instance.
    if (!el.classList.contains('swiper-initialized')) return;
    applyPeek(el); // idempotent — re-points to the 'auto' peek config
    // Re-gate on EVERY scan (init + resize + section:load), not just when applyPeek did work: a
    // viewport / breakpoint change flips fits<->overflows without necessarily re-creating the Swiper,
    // and the alreadyPeeking guard would otherwise short-circuit applyPeek before this runs.
    syncOverflow(el);

    // Attach a MutationObserver the first time we see this element. When the shared init re-creates
    // the Swiper instance (calling `new Swiper(...)` on window resize / section reload), it toggles
    // the `swiper-initialized` class. We catch that mutation and immediately re-apply peek — before
    // the browser paints the intermediate numeric-slidesPerView state — eliminating the flicker.
    if (observed.has(el)) return;
    observed.add(el);
    new MutationObserver(() => {
      if (!el.classList.contains('swiper-initialized')) return;
      const sw = el.swiper;
      if (!sw || sw.destroyed) return;
      applyPeek(el);
      syncOverflow(el);
    }).observe(el, { attributes: true, attributeFilter: ['class'] });
  });
}

let booted = false;

function boot() {
  if (booted) return;
  booted = true;

  // 1) Settle window — the shared init creates (and may re-create) the Swiper a few times while the
  //    section fetches its products and images load. Poll briefly so we land 'auto' as soon as the
  //    instance is initialised and re-heal those early re-creates. ~6s at 150ms.
  let ticks = 0;
  const settle = window.setInterval(() => {
    scan();
    if (++ticks > 40) window.clearInterval(settle);
  }, 150);

  // 2) Genuine width changes (orientation flip, desktop window resize) — re-apply peek on the
  //    trailing edge so we win after the shared init re-creates the instance.
  //    Height-only resize events (mobile address bar show/hide) are ignored here; the
  //    MutationObserver above handles any Swiper re-creation that happens during those.
  let timer = 0;
  let lastWidth = window.innerWidth;
  const reapply = () => {
    const currentWidth = window.innerWidth;
    if (currentWidth === lastWidth) return;
    lastWidth = currentWidth;
    window.clearTimeout(timer);
    timer = window.setTimeout(scan, 200);
  };
  window.addEventListener('resize', reapply, { passive: true });
  window.addEventListener('orientationchange', reapply, { passive: true });
  document.addEventListener('shopify:section:load', reapply);
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', boot);
} else {
  boot();
}

export default boot;
